import Swal from "sweetalert2";

function showError(err) {
  if (!err.accessDenied) {
    Swal.fire({
      title: "Error",
      text:
        err?.data?.message ||
        err?.data?.error?.message ||
        "Something went wrong...",
      icon: "error",
    });
  }
}

export default {
  namespaced: true,
  state: {
    groups: [],
    isLoading: false,
    isSaving: false,
    isLoaded: false,
  },
  mutations: {
    SET_STATE: (state, obj) => {
      state[`${obj.key}`] = obj.value;
    },
  },
  actions: {
    // Admin (Settings > Prompt Templates): always refetches the full list.
    getPromptTemplates({ commit }) {
      commit("SET_STATE", { key: "isLoading", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http.get("settings/prompt-templates").then(
          (res) => {
            commit("SET_STATE", { key: "groups", value: res.data.data || [] });
            commit("SET_STATE", { key: "isLoading", value: false });
            commit("SET_STATE", { key: "isLoaded", value: true });
            resolve(res.data.data);
          },
          (err) => {
            commit("SET_STATE", { key: "isLoading", value: false });
            showError(err);
            resolve();
          }
        );
      });
    },

    // Oliver's Prompt Templates panel: fetch-once per page/session, silent on
    // failure — the panel just renders no prompts, the rest of Oliver is
    // unaffected.
    fetchPromptTemplates({ commit, state }) {
      if (state.isLoaded || state.isLoading) return Promise.resolve();
      commit("SET_STATE", { key: "isLoading", value: true });
      let vm = this;
      return vm.$http
        .get("prompt-templates")
        .then((res) => {
          commit("SET_STATE", { key: "groups", value: res.data.data || [] });
        })
        .catch((err) => {
          if (!err.accessDenied) console.error(err);
        })
        .finally(() => {
          commit("SET_STATE", { key: "isLoading", value: false });
          commit("SET_STATE", { key: "isLoaded", value: true });
        });
    },

    createGroup({ commit, dispatch }, { name, color, admins_only }) {
      commit("SET_STATE", { key: "isSaving", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .post("settings/prompt-templates/groups", {
            name,
            color,
            admins_only,
          })
          .then(
            () => {
              commit("SET_STATE", { key: "isSaving", value: false });
              dispatch("getPromptTemplates").then(() => resolve(true));
            },
            (err) => {
              commit("SET_STATE", { key: "isSaving", value: false });
              showError(err);
              resolve(false);
            }
          );
      });
    },

    updateGroup({ commit, dispatch }, { id, name, color, admins_only }) {
      commit("SET_STATE", { key: "isSaving", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .put(`settings/prompt-templates/groups/${id}`, {
            name,
            color,
            admins_only,
          })
          .then(
            () => {
              commit("SET_STATE", { key: "isSaving", value: false });
              dispatch("getPromptTemplates").then(() => resolve(true));
            },
            (err) => {
              commit("SET_STATE", { key: "isSaving", value: false });
              showError(err);
              resolve(false);
            }
          );
      });
    },

    deleteGroup({ commit, dispatch }, id) {
      commit("SET_STATE", { key: "isSaving", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http.delete(`settings/prompt-templates/groups/${id}`).then(
          () => {
            commit("SET_STATE", { key: "isSaving", value: false });
            dispatch("getPromptTemplates").then(() => resolve(true));
          },
          (err) => {
            commit("SET_STATE", { key: "isSaving", value: false });
            showError(err);
            resolve(false);
          }
        );
      });
    },

    reorderGroups({ dispatch }, ids) {
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .put("settings/prompt-templates/groups/reorder", { ids })
          .then(
            () => {
              dispatch("getPromptTemplates").then(() => resolve(true));
            },
            (err) => {
              showError(err);
              dispatch("getPromptTemplates").then(() => resolve(false));
            }
          );
      });
    },

    createPrompt(
      { commit, dispatch },
      { groupId, label, value, icon, admins_only }
    ) {
      commit("SET_STATE", { key: "isSaving", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .post("settings/prompt-templates/prompts", {
            prompt_template_group_id: groupId,
            label,
            value,
            icon,
            admins_only,
          })
          .then(
            () => {
              commit("SET_STATE", { key: "isSaving", value: false });
              dispatch("getPromptTemplates").then(() => resolve(true));
            },
            (err) => {
              commit("SET_STATE", { key: "isSaving", value: false });
              showError(err);
              resolve(false);
            }
          );
      });
    },

    updatePrompt(
      { commit, dispatch },
      { id, label, value, icon, admins_only }
    ) {
      commit("SET_STATE", { key: "isSaving", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .put(`settings/prompt-templates/prompts/${id}`, {
            label,
            value,
            icon,
            admins_only,
          })
          .then(
            () => {
              commit("SET_STATE", { key: "isSaving", value: false });
              dispatch("getPromptTemplates").then(() => resolve(true));
            },
            (err) => {
              commit("SET_STATE", { key: "isSaving", value: false });
              showError(err);
              resolve(false);
            }
          );
      });
    },

    deletePrompt({ commit, dispatch }, id) {
      commit("SET_STATE", { key: "isSaving", value: true });
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http.delete(`settings/prompt-templates/prompts/${id}`).then(
          () => {
            commit("SET_STATE", { key: "isSaving", value: false });
            dispatch("getPromptTemplates").then(() => resolve(true));
          },
          (err) => {
            commit("SET_STATE", { key: "isSaving", value: false });
            showError(err);
            resolve(false);
          }
        );
      });
    },

    reorderPrompts({ dispatch }, { groupId, ids }) {
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .put(`settings/prompt-templates/groups/${groupId}/prompts/reorder`, {
            ids,
          })
          .then(
            () => {
              dispatch("getPromptTemplates").then(() => resolve(true));
            },
            (err) => {
              showError(err);
              dispatch("getPromptTemplates").then(() => resolve(false));
            }
          );
      });
    },
  },
};
